import React, { useState, useEffect } from "react"
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    InputAdornment,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material"
import { container } from "../../infrastructure/container"
import { CreateSupportUsers } from "../../infrastructure/models/transport/request/createSupportUsers"
import { SessionService } from "../../infrastructure/services/session"
import { FranchiseBriefResponse } from "../../infrastructure/models/transport/response/franchiseBrief"
import { Franchise } from "../../infrastructure/models/common/franchise"
import { SupportRole } from "../../infrastructure/models/common/supportrole"

/* ---- props ------------------------------------------------------ */
interface CreateSupportUsersProps {
    onSubmit: (data: CreateSupportUsers) => void
    currentUserRole: SupportRole
    apiError?: string | null
}

const CreateSupportUserForm: React.FC<CreateSupportUsersProps> = ({ onSubmit, currentUserRole, apiError }) => {
    /* ----- form state --------------------------------------------- */
    const [formData, setFormData] = useState<CreateSupportUsers>({
        firstname: "",
        lastname: "",
        phone: "",
        email: "",
        franchises: [],
        address: { street: "", city: "", state: "", postalCode: "" },
        role: SupportRole.SUPERADMIN // default role
    })

    const getVisibleRoles = (role: SupportRole): [string, number][] => {
        let allowedRoles: SupportRole[] = []

        switch (role) {
            case SupportRole.SUPERADMIN:
                allowedRoles = [
                    SupportRole.SUPERADMIN,
                    SupportRole.MANAGER,
                    SupportRole.ADMIN,
                    SupportRole.SUPPORT,
                ]
                break

            case SupportRole.MANAGER:
                allowedRoles = [
                    SupportRole.SUPPORT,
                ]
                break

            case SupportRole.ADMIN:
                allowedRoles = [
                    SupportRole.MANAGER,
                    SupportRole.ADMIN,
                    SupportRole.SUPPORT,
                ]
                break

            default:
                allowedRoles = [] // support or others
        }

        return (Object.entries(SupportRole) as [string, number][])
            .filter(([key]) => isNaN(Number(key))) // keep only forward mappings
            .filter(([_, val]) => allowedRoles.includes(val as SupportRole))
    }

    /* ----- checkbox options --------------------------------------- */
    const [franchiseOptions, setFranchiseOptions] = useState<
        { id: string; name: string }[]
    >([])

    /* ----- load franchises from SessionService -------------------- */
    useEffect(() => {
        const sessionService = container.resolve<SessionService>("sessionService")

        const brief: FranchiseBriefResponse[] = sessionService.franchiseBrief
        const opts =
            brief.length > 0
                ? brief.map(b => ({ id: b.franchiseId, name: b.name }))
                : sessionService.franchises.map((f: Franchise) => ({
                    id: f.franchiseId,
                    name: f.name,
                }))

        setFranchiseOptions(opts)
    }, [])

    const [errors, setErrors] = useState<Record<string, string>>({})

    const validateForm = () => {
        const newErrors: Record<string, string> = {}

        if (!formData.firstname.trim()) newErrors.firstname = "First name is required"
        if (!formData.lastname.trim()) newErrors.lastname = "Last name is required"
        if (!formData.email.trim()) newErrors.email = "Email is required"
        if (!formData.phone || formData.phone.length !== 13)
            newErrors.phone = "Valid phone number is required"

        if (!formData.address.street.trim())
            newErrors["address.street"] = "Street is required"
        if (!formData.address.city.trim())
            newErrors["address.city"] = "City is required"
        if (!formData.address.state.trim())
            newErrors["address.state"] = "State is required"
        if (!formData.address.postalCode.trim())
            newErrors["address.postalCode"] = "Postal code is required"

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setErrors((prev) => ({ ...prev, [name]: "" }))

        if (name === "role") {
            setFormData(p => ({ ...p, role: Number(value) as SupportRole }))
        } else if (name.startsWith("address.")) {
            const key = name.split(".")[1] as keyof typeof formData.address
            setFormData(p => ({ ...p, address: { ...p.address, [key]: value } }))
        } else {
            setFormData(p => ({ ...p, [name]: value }))
        }
        if (errors.email) {
            setErrors(prev => ({ ...prev, email: "" }))
        }
    }

    const handleSubmit = () => {
        if (!validateForm()) return
        onSubmit(formData)
    }

    /* ----- checkbox toggle ---------------------------------------- */
    const toggleFranchise = (id: string) => {
        setFormData(prev => ({
            ...prev,
            franchises: prev.franchises.includes(id)
                ? prev.franchises.filter(fid => fid !== id)
                : [...prev.franchises, id],
        }))
    }

    return (
        <Box
            sx={{
                p: 4,
                border: "1px solid #ccc",
                borderRadius: "8px",
                backgroundColor: "#fff",
            }}
        >
            <Typography variant="h5" gutterBottom>
                Add Support User
            </Typography>

            {apiError === "Email_Already_Registered" && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography fontWeight={600}>
                        Support User already exists
                    </Typography>
                    <Typography variant="body2">
                        A Support User with the same <b>Email</b> or <b>Phone Number</b> is already active.
                        <br />
                        Please use a different Email or Phone Number.
                    </Typography>
                </Alert>
            )}

            {apiError && apiError !== "Email_Already_Registered" && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {apiError}
                </Alert>
            )}

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    {/* firstname */}
                    <TextField
                        fullWidth
                        label="First Name"
                        name="firstname"
                        value={formData.firstname}
                        onChange={handleChange}
                        error={!!errors.firstname}
                        helperText={errors.firstname}
                    />
                </Grid>

                <Grid size={{ xs: 6, sm: 6 }}>
                    {/* lastname */}
                    <TextField
                        fullWidth
                        label="Last Name"
                        name="lastname"
                        value={formData.lastname}
                        onChange={handleChange}
                        error={!!errors.lastname}
                        helperText={errors.lastname}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    {/* role dropdown */}
                    <TextField
                        select
                        fullWidth
                        label="Role"
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                    >
                        {getVisibleRoles(currentUserRole).map(([label, val]) => (
                            <MenuItem key={val} value={val}>
                                {label.replace(/_/g, " ")}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                <Grid size={{ xs: 6, sm: 6 }}>
                    <TextField
                        fullWidth
                        required
                        label="Phone"
                        name="phone"
                        value={formData.phone.replace("+91", "")}
                        error={!!errors.phone}
                        helperText={errors.phone}
                        onChange={(e) => {
                            const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10)
                            setFormData((prev) => ({
                                ...prev,
                                phone: `+91${digitsOnly}`
                            }))
                        }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">+91</InputAdornment>
                        }}
                        inputProps={{
                            maxLength: 10,
                            pattern: "\\d{10}"
                        }}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 12 }}>
                    {/* Email */}
                    <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={!!errors.email}
                        helperText={errors.email}
                    />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Box
                        sx={{
                            border: "1px solid #ccc",
                            borderRadius: 2,
                            p: 2,
                            position: "relative",
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{
                                position: "absolute",
                                top: "-12px",
                                left: "16px",
                                backgroundColor: "#fff",
                                px: 1
                            }}
                        >
                            Select Franchises
                        </Typography>
                        <Grid container spacing={1}>
                            {franchiseOptions.map(fr => (
                                <Grid key={fr.id} size={{ xs: 12, sm: 4 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.franchises.includes(fr.id)}
                                                onChange={() => toggleFranchise(fr.id)}
                                            />
                                        }
                                        label={fr.name}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Grid>

                {/* address */}
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        label="Street"
                        name="address.street"
                        value={formData.address.street}
                        onChange={handleChange}
                        error={!!errors["address.street"]}
                        helperText={errors["address.street"]}
                    />
                </Grid>

                <Grid size={{ xs: 5 }}>
                    <TextField
                        fullWidth
                        label="City"
                        name="address.city"
                        value={formData.address.city}
                        onChange={handleChange}
                        error={!!errors["address.city"]}
                        helperText={errors["address.city"]}
                    />
                </Grid>

                <Grid size={{ xs: 5 }}>
                    <TextField
                        fullWidth
                        label="State"
                        name="address.state"
                        value={formData.address.state}
                        onChange={handleChange}
                        error={!!errors["address.state"]}
                        helperText={errors["address.state"]}
                    />
                </Grid>

                <Grid size={{ xs: 2 }}>
                    <TextField
                        fullWidth
                        label="Postal Code"
                        name="address.postalCode"
                        value={formData.address.postalCode}
                        onChange={handleChange}
                        inputProps={{ maxLength: 6 }}
                        error={!!errors["address.postalCode"]}
                        helperText={errors["address.postalCode"]}
                    />
                </Grid>
            </Grid>

            <Button
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                onClick={handleSubmit}
            >
                Submit
            </Button>
        </Box>
    )
}

export default CreateSupportUserForm